import { Injectable, NgZone } from '@angular/core';
import { ListHelperService, ProfileService, StoreService } from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { MarsLicenseService } from 'app/mars/services/mars-licenses.service';

@Injectable()
export class MarsGroupLicenseTableService {
  userRole: any;
  license: any;
  dataSource: any;
  mode: string = 'view';
  constructor(
    public marsLicenseListService: MarsLicenseService,
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService,
    public ngZone: NgZone
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  async getDatasource(listId, options: any = {}, group: any) {
    this.dataSource = new TableDatasource({
      sort: 'name',
      columns: this.getColumns(group),
      load: (props) => this.fetch(props, this.dataSource),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await this.dataSource.refreshData();
    StoreService.create(listId, this.dataSource);
    return this.dataSource;
  }

  fetch({ page, limit, sort, search, filters }, dataSource: TableDatasource) {
    const { groupId, ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());
    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      groupId,
      $filter
    };
    return this.marsLicenseListService.fetch(paginationOptions).then((response) => response);
  }

  getDatasourceFilters(dataSource: TableDatasource) {
    const { groupId, ...$filterValues } = dataSource.filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());
    return {
      $filter,
      groupId
    };
  }

  getColumns(group) {
    const columns = [
      {
        source: 'name',
        label: this.translate.instant('nouns.name'),
        dataType: DataTypes.String,
        sortable: true,
        valueParser: (value) => {
          return value || '-';
        }
      },
      {
        source: 'surname',
        label: this.translate.instant('nouns.surname'),
        dataType: DataTypes.String,
        sortable: true,
        valueParser: (value) => {
          return value || '-';
        }
      },
      group.isAnonymous && {
        source: 'alias',
        label: this.translate.instant('nouns.alias'),
        dataType: DataTypes.String,
        sortable: true,
        valueParser: (value) => {
          return value || '-';
        }
      },
      {
        source: 'userEmail',
        label: this.translate.instant('nouns.email'),
        dataType: DataTypes.String,
        sortable: true,
        cellRenderer: (renderer, value) => renderer`<div class="ellipsis" title="${value}">${value}</div>`
      },
      {
        source: 'federation',
        label: this.translate.instant('mars.player.header.federation'),
        dataType: DataTypes.String,
        sortable: true
      },
      {
        source: 'lang',
        label: this.translate.instant('mars.player.header.lang'),
        sortable: true
      },
      {
        source: 'status',
        style: { width: '175px' },
        label: this.translate.instant('nouns.status'),
        sortable: false,
        valueParser: (value) => this.translate.instant(`mars.player.status.${value}`),
        searchRenderer: (renderer, value, callback) => {
          const options = [
            { key: 'created', value: this.translate.instant('mars.player.status.created') },
            { key: 'active', value: this.translate.instant('mars.player.status.active') },
            { key: 'completed', value: this.translate.instant('mars.player.status.completed') },
            { key: 'cancelled', value: this.translate.instant('mars.player.status.cancelled') }
          ];
          return renderer`
            <gl-select small multiselect
              .options="${options}"
              .value="${value || 'any'}"
              @onChange="${(e) => callback(e.detail.value)}"
            >
            </gl-select>`;
        }
      },
      {
        source: 'tags',
        label: this.translate.instant('nouns.tags'),
        dataType: DataTypes.array
      },
      {
        alignment: 'right',
        showInColumnChooser: false,
        style: { width: '60px' },
        cellRenderer: (renderer, value, data) => {
          return renderer`
            <gl-context-menu medium>
              <gl-button slot="target" id="${data.id}" icon icon-center="glicon-dots"></gl-button>
              <gl-menu-item text="${this.translate.instant('mars.player.editInfo')}" icon="" @click="${() =>
            this.changeView(data, 'edit')}"></gl-menu-item>
              <gl-menu-item text="${this.translate.instant('mars.player.showProgress')}" @click="${() =>
            this.changeView(data, 'progress')}"></gl-menu-item>
            </gl-context-menu>`;
        }
      }
    ].filter((column) => !!column);
    return columns;
  }

  changeView(license, mode) {
    this.ngZone.run(() => {
      this.license = license;
      this.mode = mode;
    });
  }

  anonymizeLicense(license) {
    this.marsLicenseListService
      .anonymizeLicense(license)
      .then(() => this.dataSource.refreshData())
      .catch((err) => console.error(err));
  }

  getCustomFilters() {
    return {};
  }
}
